'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var objectSpread2 = require('./../../external/@babel/runtime/helpers/esm/objectSpread2.js');
var objectWithoutProperties = require('./../../external/@babel/runtime/helpers/esm/objectWithoutProperties.js');
var _extends = require('./../../external/@babel/runtime/helpers/esm/extends.js');
var PropTypes = require('prop-types');
var React = require('react');
var reachRouter = require('@gatsbyjs/reach-router');
var index = require('../index.js');
var useOnScreen = require('../hooks/useOnScreen.js');
var parsePath = require('./parse-path.js');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var PropTypes__default = /*#__PURE__*/_interopDefault(PropTypes);
var React__default = /*#__PURE__*/_interopDefault(React);

var _excluded = ["children", "href", "getProps", "onClick", "onMouseEnter", "activeClassName", "activeStyle", "innerRef", "partiallyActive", "state", "replace", "_location"];
var isAbsolutePath = function isAbsolutePath(path) {
  return path === null || path === void 0 ? void 0 : path.startsWith('/');
};
var withPrefix = function withPrefix(path) {
  var _ref;
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : getGlobalBasePrefix();
  if (path.startsWith('./') || path.startsWith('../')) {
    return path;
  }
  var base = (_ref = prefix !== null && prefix !== void 0 ? prefix : getGlobalPathPrefix()) !== null && _ref !== void 0 ? _ref : '/';
  return "".concat(base !== null && base !== void 0 && base.endsWith('/') ? base.slice(0, -1) : base).concat(path.startsWith('/') ? path : "/".concat(path));
};

// These global values are wrapped in typeof clauses to ensure the values exist.
// This is especially problematic in unit testing of this component.
var getGlobalPathPrefix = function getGlobalPathPrefix() {
  return process.env.NODE_ENV !== 'production' ? typeof __PATH_PREFIX__ !== 'undefined' ? __PATH_PREFIX__ : undefined : __PATH_PREFIX__;
};
var getGlobalBasePrefix = function getGlobalBasePrefix() {
  return process.env.NODE_ENV !== 'production' ? typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : undefined : __BASE_PATH__;
};
var absolutify = function absolutify(path, current) {
  // If it's already absolute, return as-is
  if (isAbsolutePath(path)) {
    return path;
  }
  return reachRouter.resolve(path, current);
};
var rewriteLinkPath = function rewriteLinkPath(path, location) {
  var _location$pathname;
  var relativeTo = (_location$pathname = location.pathname) !== null && _location$pathname !== void 0 ? _location$pathname : '';
  if (typeof path === 'number') {
    return path;
  }
  if (index.isExternalLink(path, location)) {
    return path;
  }
  return isAbsolutePath(path) ? withPrefix(path) : absolutify(path, relativeTo);
};
var NavLinkPropTypes = {
  activeClassName: PropTypes__default.default.string,
  activeStyle: PropTypes__default.default.object,
  partiallyActive: PropTypes__default.default.bool
};
var GatsbyLinkLocationWrapper = function GatsbyLinkLocationWrapper(props) {
  return /*#__PURE__*/React__default.default.createElement(reachRouter.Location, null, function (_ref2) {
    var location = _ref2.location;
    return /*#__PURE__*/React__default.default.createElement(GatsbyLink, _extends.default({}, props, {
      _location: location
    }));
  });
};
var GatsbyLink = function GatsbyLink(props) {
  var children = props.children,
    href = props.href,
    _props$getProps = props.getProps,
    getProps = _props$getProps === void 0 ? defaultGetProps : _props$getProps,
    _onClick = props.onClick,
    _onMouseEnter = props.onMouseEnter;
    props.activeClassName;
    var $activeStyle = props.activeStyle;
    props.innerRef;
    var partiallyActive = props.partiallyActive,
    state = props.state,
    replace = props.replace,
    _location = props._location,
    rest = objectWithoutProperties.default(props, _excluded);
  var prefixedTo = rewriteLinkPath(href, _location.pathname);
  if (index.isExternalLink(prefixedTo, _location)) {
    return /*#__PURE__*/React__default.default.createElement("a", _extends.default({
      href: href
    }, rest), children);
  }
  var innerRef = React.useRef(null);
  var isOnScreen = useOnScreen.useOnScreen(innerRef);
  React.useEffect(function () {
    if (isOnScreen) {
      _prefetch();
    }
  });
  var _prefetch = function _prefetch() {
    var currentPath = _location && _location.pathname ? _location.pathname : window.location.pathname;
    var rewrittenPath = rewriteLinkPath(href, _location);
    var newPathName = parsePath.parsePath(rewrittenPath).pathname;

    // Prefech is used to speed up next navigations. When you use it on the current navigation,
    // there could be a race-condition where Chrome uses the stale data instead of waiting for the network to complete
    if (currentPath !== newPathName) {
      ___loader.enqueue(newPathName);
    }
  };
  var defaultGetProps = function defaultGetProps(_ref3) {
    var isPartiallyCurrent = _ref3.isPartiallyCurrent,
      isCurrent = _ref3.isCurrent;
    if (partiallyActive ? isPartiallyCurrent : isCurrent) {
      return {
        className: [className, activeClassName].filter(Boolean).join(' '),
        style: objectSpread2.default(objectSpread2.default({}, style), $activeStyle)
      };
    }
    return null;
  };
  return /*#__PURE__*/React__default.default.createElement(reachRouter.Link, _extends.default({
    to: prefixedTo,
    state: state,
    getProps: getProps,
    ref: innerRef,
    onMouseEnter: function onMouseEnter(e) {
      if (_onMouseEnter) {
        _onMouseEnter(e);
      }
      ___loader.hovering(parsePath.parsePath(prefixedTo).pathname);
    },
    onClick: function onClick(e) {
      if (_onClick) {
        _onClick(e);
      }
      if (e.button === 0 &&
      // ignore right clicks
      !props.target &&
      // let browser handle "target=_blank"
      !e.defaultPrevented &&
      // onClick prevented default
      !e.metaKey &&
      // ignore clicks with modifier keys...
      !e.altKey && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        var shouldReplace = replace;
        var isCurrent = encodeURI(prefixedTo) === _location.pathname;
        if (typeof replace !== 'boolean' && isCurrent) {
          shouldReplace = true;
        }
        // Make sure the necessary scripts and data are
        // loaded before continuing.
        window.___navigate(prefixedTo, {
          state: state,
          replace: shouldReplace
        });
      }
      return true;
    }
  }, rest), children);
};
GatsbyLink.propTypes = objectSpread2.default(objectSpread2.default({}, NavLinkPropTypes), {}, {
  href: PropTypes__default.default.string.isRequired,
  onClick: PropTypes__default.default.func,
  replace: PropTypes__default.default.bool,
  state: PropTypes__default.default.object
});
var GatsbyLink$1 = /*#__PURE__*/React__default.default.forwardRef(function (props, ref) {
  return /*#__PURE__*/React__default.default.createElement(GatsbyLinkLocationWrapper, _extends.default({
    innerRef: ref
  }, props));
});

exports.parsePath = parsePath.parsePath;
exports.default = GatsbyLink$1;
//# sourceMappingURL=index.js.map
